import { LIST_INDEX_WORKERS_NAME } from "@/lib/ai/tools/list-index-workers/constants";
import type { ListIndexWorkersOutput } from "@/lib/ai/tools/list-index-workers/server";
import { defineDatasetSpec } from "@/lib/ai/visualization/data/adapters/adapter-contract";
import {
  buildListTableDataset,
  createTableOnlyProfile,
  toListNumber,
  toListText,
} from "@/lib/ai/visualization/data/adapters/dataset-normalization";
import type {
  VizDataset,
  VizDatasetRow,
} from "@/lib/ai/visualization/data/schema";

const INDEX_WORKERS_DATASET_SPEC = defineDatasetSpec({
  id: `${LIST_INDEX_WORKERS_NAME}-table`,
  profile: createTableOnlyProfile({ datasetFamily: "list.indexWorkers" }),
  fields: [
    { key: "workerId", role: "dim", type: "string" },
    { key: "workerName", role: "dim", type: "string" },
    { key: "note", role: "dim", type: "string" },
    { key: "urlType", role: "dim", type: "string" },
    { key: "schedule", role: "dim", type: "string" },
    { key: "samplingCount", role: "metric", type: "number", unit: "count" },
    { key: "urlCount", role: "metric", type: "number", unit: "count" },
    { key: "sitemap", role: "dim", type: "string" },
    { key: "rssFeed", role: "dim", type: "string" },
    { key: "rssFeedSamplingPeriod", role: "dim", type: "string" },
    { key: "propertyType", role: "dim", type: "string" },
    { key: "propertyId", role: "dim", type: "string" },
    { key: "propertyValue", role: "dim", type: "string" },
    { key: "propertyEmail", role: "dim", type: "string" },
    { key: "urlGroupOp", role: "dim", type: "string" },
    {
      key: "urlGroupPatternCount",
      role: "metric",
      type: "number",
      unit: "count",
    },
    {
      key: "ignoreURLParametersCount",
      role: "metric",
      type: "number",
      unit: "count",
    },
  ] as const,
  defaults: { category: "workerName", value: "urlCount" },
  notes: "Table dataset normalized from list_index_workers output.",
});

function toPropertyEmail(
  property: ListIndexWorkersOutput[number]["property"],
): string | null {
  if (!property) return null;
  if (
    property.__typename === "RegisteredSearchConsoleUserProperty" &&
    property.user
  ) {
    return toListText(property.user.email);
  }
  if (
    property.__typename === "RegisteredSearchConsoleServiceAccountProperty" &&
    property.serviceAccount
  ) {
    return toListText(property.serviceAccount.email);
  }
  return null;
}

function toIndexWorkerRows(output: ListIndexWorkersOutput): VizDatasetRow[] {
  return output.map((worker) => ({
    workerId: toListText(worker.id),
    workerName: toListText(worker.name),
    note: toListText(worker.note),
    urlType: toListText(worker.urlType),
    schedule: toListText(worker.schedule),
    samplingCount: toListNumber(worker.samplingCount),
    urlCount: toListNumber(Array.isArray(worker.urls) ? worker.urls.length : 0),
    sitemap: toListText(worker.sitemap),
    rssFeed: toListText(worker.rssFeed),
    rssFeedSamplingPeriod: toListText(worker.rssFeedSamplingPeriod),
    propertyType: toListText(worker.property?.__typename),
    propertyId: toListText(worker.property?.id),
    propertyValue: toListText(worker.property?.property),
    propertyEmail: toPropertyEmail(worker.property),
    urlGroupOp: toListText(worker.urlGroup?.op),
    urlGroupPatternCount: toListNumber(
      Array.isArray(worker.urlGroup?.patterns)
        ? worker.urlGroup.patterns.length
        : 0,
    ),
    ignoreURLParametersCount: toListNumber(
      Array.isArray(worker.ignoreURLParameters)
        ? worker.ignoreURLParameters.length
        : 0,
    ),
  }));
}

/**
 * list_index_workers 出力を table-only dataset へ正規化する。
 */
export function buildListIndexWorkersDatasets(
  output: unknown,
  source: VizDataset["source"],
): VizDataset[] {
  const workers = Array.isArray(output)
    ? (output as ListIndexWorkersOutput)
    : [];
  const rows = toIndexWorkerRows(workers);
  const dataset = buildListTableDataset({
    id: INDEX_WORKERS_DATASET_SPEC.id,
    datasetFamily: INDEX_WORKERS_DATASET_SPEC.profile.datasetFamily,
    variant: INDEX_WORKERS_DATASET_SPEC.profile.variant,
    source,
    rows,
    fields: INDEX_WORKERS_DATASET_SPEC.fields,
    defaults: INDEX_WORKERS_DATASET_SPEC.defaults,
    notes: INDEX_WORKERS_DATASET_SPEC.notes,
  });

  return dataset ? [dataset] : [];
}
